import React, {useEffect, useMemo, useState} from 'react';
import {Box, Chip, LinearProgress, List, ListItem, ListItemText, Paper, Stack, Tooltip, Typography} from '@mui/material';
import {getReportsByICRNumber} from '../../../firebase/firestore/reports';
import {resolveIcrNumber} from '../../../firebase/firestore/icr';
import AuthorChip from '../../products/components/common/AuthorChip';

function resolveProductType(report) {
    return report?.productType?.value || report?.meta?.productType?.value || '';
}

/** Linked ICR number on a report. */
function resolveReportIcrNumber(report) {
    return report?.meta?.icrNumber
        || report?.icrNumber
        || '';
}

/** FINTEL (approved analytic) or FINTEL draft — not pitch, sitrep, disclosure, etc. */
function isFintelProduct(report) {
    const productType = resolveProductType(report).toLowerCase();
    if (!productType || productType === 'pitch' || productType === 'disclosure') return false;
    return productType === 'analytic' || productType === 'fintel';
}

function isDraftProduct(report) {
    const status = (report?.status || '').toLowerCase();
    return status !== 'approved';
}

/** Related products: FINTELs linked to this ICR via meta.icrNumber. */
function filterRelatedFintelProducts(reports, icrNumber) {
    if (!icrNumber) return [];
    return (reports || []).filter((report) => {
        if (!isFintelProduct(report)) return false;
        return resolveReportIcrNumber(report) === icrNumber;
    });
}

const IcrProducts = ({icr}) => {
    const icrNumber = resolveIcrNumber(icr);

    const [reports, setReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(false);
    const [reportsError, setReportsError] = useState(null);

    useEffect(() => {
        let mounted = true;
        if (!icrNumber) {
            setReports([]);
            setReportsError(null);
            setLoadingReports(false);
            return () => {
                mounted = false;
            };
        }

        setLoadingReports(true);
        setReportsError(null);
        getReportsByICRNumber(icrNumber)
            .then(res => {
                if (mounted) setReports(res || []);
            })
            .catch(err => {
                console.error('getReportsByICRNumber error', err);
                if (mounted) setReportsError(err);
            })
            .finally(() => {
                if (mounted) setLoadingReports(false);
            });

        return () => {
            mounted = false;
        };
    }, [icrNumber]);

    const relatedProducts = useMemo(
        () => filterRelatedFintelProducts(reports, icrNumber)
            .sort((a, b) => (b.updatedAt || b.createdAt || '').localeCompare(a.updatedAt || a.createdAt || '')),
        [reports, icrNumber],
    );

    return (
        <Paper elevation={0}
               sx={{flex: 1, pb: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider'}}>
            <Stack>
                <Typography sx={{p: 2, pb: 1}} fontWeight={600}>Related Products</Typography>

                {icrNumber &&
                loadingReports ? (
                    <Stack p={2} pt={1}><LinearProgress/></Stack>
                ) : reportsError ? (
                    <Typography variant="body2" color="error">Error loading reports</Typography>
                ) : (relatedProducts.length > 0 ? (
                    <List disablePadding>
                        {relatedProducts.map((r, i) => (
                            <ListItem
                                component={'a'}
                                href={`/products/${r.id}`}
                                target="_blank"
                                key={r.id ?? i}
                                sx={{py: 0.5, cursor: 'pointer'}}
                                button
                            >
                                <ListItemText
                                    primaryTypographyProps={{component: 'div'}}
                                    secondaryTypographyProps={{component: 'div'}}
                                    primary={
                                        <Stack
                                            data-testid="related-product-title-row"
                                            direction="row"
                                            alignItems="flex-start"
                                            spacing={0.75}
                                            sx={{mb: 0.75}}
                                        >
                                            {isDraftProduct(r) && (
                                                <Chip
                                                    label="DRAFT"
                                                    size="small"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: '0.65rem',
                                                        fontWeight: 700,
                                                        mt: '2px',
                                                        '& .MuiChip-label': {px: 0.75},
                                                    }}
                                                />
                                            )}
                                            <Typography variant="body2" sx={{fontWeight: 500}}>
                                                {r?.meta?.title || r?.title || r.id}
                                            </Typography>
                                        </Stack>
                                    }
                                    secondary={r.userId ? (
                                        <Box
                                            component="span"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <AuthorChip
                                                userId={r.userId}
                                                userDisplayName={
                                                    r.authorProfiles?.[r.userId]?.displayName
                                                    || r.userDisplayName
                                                    || r.userId
                                                }
                                                flex={undefined}
                                                avatarProfile={r.authorProfiles?.[r.userId] ?? null}
                                            />
                                        </Box>
                                    ) : (r.userDisplayName || null)}
                                />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Typography sx={{px: 2, pb: 1}} variant="body2" color="text.secondary">
                        No{' '}
                        <Tooltip title="Finished Intelligence" arrow placement="top">
                            <abbr style={{textDecoration: 'underline dotted', cursor: 'help'}}>FINTEL</abbr>
                        </Tooltip>
                        {' '}products linked to this ICR yet.
                    </Typography>
                ))}
            </Stack>
        </Paper>
    );
};

export default IcrProducts;
